import { useContext, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { MessageAction, MessageDTO } from '../../Types/MessageT';
import { MessageContext } from '../Context/MessageContextV2';
import { MessageS } from '../MessageS';
import { EmailFolderInfo } from '../../Types/MailT';
import { MailS } from '../MailS';
import { EventParticipationsContext } from '../Context/EventParticipationContextV2';

// eslint-disable-next-line import/prefer-default-export
export const useEventEmailFolders = (contactIds: number[]) => {
  const { box, filter, id: eventId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { changeByAction, changeMessage, message, action } = useContext(MessageContext);
  const { unreadMessagesProps } = useContext(EventParticipationsContext);
  const [messages, setMessages] = useState<MessageDTO[]>([]);
  const [selectedMessage, selectMessage] = useState<MessageDTO>();
  const [openedFolder, openFolder] = useState<EmailFolderInfo>();
  const [loading, setLoading] = useState(false);
  const [trigger, setTrigger] = useState(Date.now());
  const [loadingMessage, setLoadingMessage] = useState(false);
  const [changingSeenFlag, setChangingSeenFlag] = useState(false);
  const { increment, decrement } = unreadMessagesProps;
  const query = searchParams.get('query');
  const emailId = searchParams.get('emailId');
  const entityId = searchParams.get('entityId');

  useEffect(() => {
    const abortController = new AbortController();
    fetchMessages(abortController);
    return () => abortController.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [box, filter, query, trigger]);

  useEffect(() => {
    openFolder({
      totalMessages: messages.length,
      unreadMessages: messages.length,
      name: MailS.getFolderLabel(box ?? ''),
      src: 'BOOKITUP',
    });
  }, [messages]);

  useEffect(() => {
    if (message && action !== 'EDIT') {
      changeMessage(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMessage]);

  useEffect(() => {
    if (selectedMessage) {
      selectMessage(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [box]);

  useEffect(() => {
    if (emailId) {
      const optionalEmail = messages.find((e) => e.id === parseInt(emailId, 10));
      if (optionalEmail) {
        selectMessage(optionalEmail);
      }
    } else if (window.innerWidth > 700 && !entityId) {
      // _selectMessage(messages[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailId, messages]);

  const fetchMessages = async (abortController: AbortController) => {
    if (box) {
      try {
        setLoading(true);
        const resJSON = await MessageS.getFolderMessages(box, contactIds);
        setMessages(resJSON);
      } finally {
        if (!abortController.signal.aborted) {
          setLoading(false);
        }
      }
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-shadow
  const changeEmail = (msg: MessageDTO) =>
    setMessages(
      messages.map((m) => ((m.id && m.id === msg.id) || (m.stringId && m.stringId === msg.stringId) ? msg : m)),
    );

  const prepareMessage = async (selectedAction: MessageAction) => {
    if (selectedMessage) {
      // General postbox with assigned event --> Redirect to event scope
      if (!eventId && selectedMessage.eventId) {
        navigate(
          `/events/${selectedMessage.eventId}/emails/${box}?action=${selectedAction}&messageId=${selectedMessage.id}`,
        );
      } else {
        // We are already in event scope or message doesn't have an event assigned yet
        if (selectedMessage.imap) {
          // Setup manually eventId since its not populated from MailS
          selectedMessage.eventId = Number(eventId);
          changeByAction(null, selectedAction, selectedMessage);
          return;
        }
        changeByAction(selectedMessage.id, selectedAction);
      }
    }
  };

  const flushMessage = () => {
    if (selectedMessage) {
      changeEmail(selectedMessage);
    }
    changeMessage(null);
  };

  const refresh = () => setTrigger(Date.now());

  const loadFullMessage = async (msg: MessageDTO) => {
    if (msg) {
      const { outboundConfigId, fullNameFolder } = msg;
      if (outboundConfigId && fullNameFolder) {
        setLoadingMessage(true);
        const fullMessage = await MailS.getMessageIMAP(msg.id ?? msg.stringId, outboundConfigId, fullNameFolder);
        setLoadingMessage(false);
        if (fullMessage) {
          selectMessage(fullMessage);
        }
      }
    }
  };

  // eslint-disable-next-line no-underscore-dangle
  const _selectMessage = (msg?: MessageDTO) => {
    if (msg && msg.imap) {
      loadFullMessage(msg);
    }
    selectMessage(msg);
  };

  const changeSeenFlag = async (read: boolean) => {
    try {
      if (selectedMessage) {
        setChangingSeenFlag(true);
        const { id, stringId, imap, fullNameFolder, outboundConfigId } = selectedMessage;
        if (imap && fullNameFolder && outboundConfigId) {
          const ok = await MailS.changeMessageSeenFlagIMAP(id ?? stringId, outboundConfigId, fullNameFolder, read);
          if (ok) {
            changeEmail({ ...selectedMessage, readTimestamp: read ? new Date() : null });
            if (read) {
              decrement();
            } else {
              increment();
            }
          }
        } else {
          const patchedEmail = await MessageS.patchEmail(selectedMessage.id, { read });
          if (patchedEmail) {
            changeEmail(patchedEmail);
            if (read) {
              decrement();
            } else {
              increment();
            }
          }
        }
      }
    } finally {
      setChangingSeenFlag(false);
    }
  };

  const value = useMemo(
    () => ({
      messages,
      loading,
      selectedMessage,
      pageNumber: 0,
      totalCount: 0,
      pageCount: 0,
      prepareMessage,
      changeEmail,
      selectMessage: _selectMessage,
      setPageNumber: () => null,
      refresh,
      flushMessage,
      changeSeenFlag,
      changingSeenFlag,
      openFolder: () => null,
      openedFolder,
      isBookitupScope: true,
      loadingMessage,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [messages, loading, selectedMessage, openedFolder, loadingMessage, changingSeenFlag],
  );

  return value;
};
